/**
 * PerformanceTab — top-level dossier tab for the supplier scorecard.
 *
 * §5.BT §3.3 P3 Commit C (2026-06-19). Locked in
 *   docs/audits/2026-06-18_5bt_p3_3_dossier_p2_design.md §D + §G
 *
 * Responsibilities:
 *   - Frontend gate (Layer 3 of the three-layer PROD-invisibility defense):
 *     when `scorecardUiEnabled` is FALSE, this component renders null and the
 *     parent excludes the tab from the strip entirely.
 *   - Lazy fetches `GET /api/suppliers/{supplierId}/scorecard?quarters=5`
 *     on first mount (parent triggers mount via tab visit).
 *   - Renders six states: loading / resolving / error / disabled / no-data / success.
 *   - Composes the four sub-components per P2 §D.2.
 *
 * `supplierId` is resolved asynchronously by the parent (name → id lookup via
 * /api/company/suppliers?search=...). While the parent's resolution is in
 * flight, `supplierId` is null and `resolutionComplete` is false — PerformanceTab
 * shows the loading spinner. If resolution completes but fails (no matching
 * supplier in this tenant's list), `resolutionComplete` is true and `supplierId`
 * is null — PerformanceTab shows the no-data empty state (semantically: this
 * supplier has no scorecard in this tenant, identical to 404 from the backend).
 *
 * Edge-case contract (P2 §D.4 + §F):
 *   - has_data === false  → friendly empty state ("No scorecard data yet for...")
 *   - flag === false      → early-null (defense-in-depth; parent should already
 *                            be hiding this tab when flag is FALSE)
 *   - quarters < 5        → renders what's there (no padding)
 *   - partial_coverage    → P2 §F badge surfaced in the Price-YoY card
 */
import React, { useEffect, useState } from 'react';
import { LoadingSpinner, ErrorMessage } from '../SharedComponents';
import KpiCardGrid from './KpiCardGrid';
import GradeTrajectoryStrip from './GradeTrajectoryStrip';
import ScorecardDrilldown from './ScorecardDrilldown';
import { API_BASE } from '../../config';

const DEFAULT_QUARTERS = 5;

// Lane 215 (Linear rebuild, Stage 2 — dossier child chrome): read the `.sd`
// Linear token layer so this tab flips with the shell in the future dark lane.
const LINEAR_PANEL = {
  background: 'var(--panel)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  boxShadow: 'var(--shadow)',
};

function PerformanceTab({
  supplierId,
  token,
  scorecardUiEnabled,
  supplierName,
  resolutionComplete = true,
  // QA-3 item 5 (Mega QA 2026-07-22): the Wingman-calculated on-time % from
  // the delivery-reliability engine (0..1 fraction), or null/undefined when
  // unavailable. Passed straight through to KpiCardGrid's On-Time-Delivery
  // card subtext -- this component holds no delivery-reliability fetch logic
  // of its own.
  wingmanOnTimePct,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Layer 3 frontend gate: parent should already be hiding the tab when flag is
  // FALSE, but render null defensively.
  //
  // Three intermediate states:
  //   - flag FALSE: render null (defense-in-depth).
  //   - resolution still in flight (supplierId === null && !resolutionComplete):
  //     stay in loading — `loading` stays true.
  //   - resolution complete + no id (supplierId === null && resolutionComplete):
  //     no-data empty state (semantically equivalent to a backend 404).
  const resolvedToNoSupplier = !supplierId && resolutionComplete;
  const skipFetch = !scorecardUiEnabled || !supplierId || !token;

  useEffect(() => {
    if (skipFetch) {
      // Resolution still in flight → keep loading.
      // Resolution complete with no id → flip out of loading so the empty
      // state renders instead of an infinite spinner.
      if (resolvedToNoSupplier) {
        setLoading(false);
      }
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(
      `${API_BASE}/api/suppliers/${encodeURIComponent(supplierId)}/scorecard?quarters=${DEFAULT_QUARTERS}`,
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
    )
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          if (res.status === 404) {
            setData({ supplier_id: supplierId, scorecard_ui_enabled: true, has_data: false, scopes: [], quarters: [] });
            setLoading(false);
            return;
          }
          throw new Error(`Scorecard request failed (${res.status})`);
        }
        const body = await res.json();
        if (cancelled) return;
        setData(body);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Could not load scorecard');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [supplierId, token, scorecardUiEnabled, skipFetch, resolvedToNoSupplier]);

  // Defense-in-depth: parent should be hiding the tab when flag is FALSE.
  if (!scorecardUiEnabled) return null;

  if (loading) {
    return (
      <div data-testid="performance-loading">
        <LoadingSpinner text="Loading scorecard..." />
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="performance-error">
        <ErrorMessage message={error} />
      </div>
    );
  }

  if (!data || data.has_data === false) {
    return (
      <div style={{ ...LINEAR_PANEL, padding: '16px' }}>
        <div
          data-testid="performance-empty"
          style={{ padding: '40px 20px', textAlign: 'center' }}
        >
          <div
            style={{
              fontSize: 'var(--font-size-md)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--text)',
              marginBottom: '6px',
            }}
          >
            No scorecard data yet
          </div>
          <p
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-3)',
              maxWidth: '440px',
              margin: '0 auto',
            }}
          >
            {supplierName ? (
              <>Once a customer scorecard for <strong>{supplierName}</strong> is ingested, KPI cards and trajectory will appear here.</>
            ) : (
              <>Once a customer scorecard is ingested for this supplier, KPI cards and trajectory will appear here.</>
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="performance-success">
      <KpiCardGrid quarters={data.quarters} wingmanOnTimePct={wingmanOnTimePct} />
      <GradeTrajectoryStrip quarters={data.quarters} />
      <ScorecardDrilldown quarters={data.quarters} scopes={data.scopes} collapsedByDefault />
    </div>
  );
}

export default PerformanceTab;
